/* eslint-disable import/order */
/* eslint-disable import/no-webpack-loader-syntax */
/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import * as React from 'react';
import { useToggle } from 'react-use';

import footer from './assets/footer.png';
import header from './assets/header.png';
import blackFont from './assets/Inter-Black.ttf';
import boldFont from './assets/Inter-Bold.ttf';
import extraBoldFont from './assets/Inter-ExtraBold.ttf';
import extraLightFont from './assets/Inter-ExtraLight.ttf';
import lightFont from './assets/Inter-Light.ttf';
import mediumFont from './assets/Inter-Medium.ttf';
import regularFont from './assets/Inter-Regular.ttf';
import semiBoldFont from './assets/Inter-SemiBold.ttf';
import thinFont from './assets/Inter-Thin.ttf';
import { PDF_SCALE_FACTOR } from './constant';
import { Html2PdfIgnorer } from './html2PdfIgnorer';
import { breakPages } from './pageBreaker';

const HEADER_COMPONENT_HEIGHT = 44;
const FOOTER_COMPONENT_HEIGHT = 44;
const HEADER_PDF_HEIGHT = 60;
const FOOTER_PDF_HEIGHT = 60;
const FONT_NAME = 'sans-serif';
const ENCODING = 'Identity-H';

interface PDFContent {
  html: HTMLElement;
  header?: HTMLElement;
  footer?: HTMLElement;
}

type FontFamily = {
  font: string;
  subsets: FontSubset[];
};

type FontSubset = {
  fileName: string;
  url: string;
  fontFamily: string;
  fontWeight: number;
  encoding?: string;
  base64?: string;
  type?: string;
};

export const DEFAULT_FONT: FontFamily = {
  font: 'Inter',
  subsets: [
    {
      fileName: 'Inter-Thin.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 100,
      url: thinFont,
    },
    {
      fileName: 'Inter-ExtraLight.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 200,
      url: extraLightFont,
    },
    {
      fileName: 'Inter-Light.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 300,
      url: lightFont,
    },
    {
      fileName: 'Inter-Regular.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 400,
      url: regularFont,
    },
    {
      fileName: 'Inter-Medium.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 500,
      url: mediumFont,
    },
    {
      fileName: 'Inter-SemiBold.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 600,
      url: semiBoldFont,
    },
    {
      fileName: 'Inter-Bold.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 700,
      url: boldFont,
    },
    {
      fileName: 'Inter-ExtraBold.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 800,
      url: extraBoldFont,
    },
    {
      fileName: 'Inter-Black.ttf',
      fontFamily: FONT_NAME,
      fontWeight: 900,
      url: blackFont,
    },
  ],
};

class PDFService {
  private doc;

  private _onBeforeRenderPdf;

  protected async loadLib() {
    if (!this.doc) {
      return import('@doltech/jspdf').then((mod) => {
        const JsPDF = mod.jsPDF;
        this.doc = new JsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: 'a4',
          compress: true,
        });
      });
    }
    return Promise.resolve(this.doc);
  }

  get onBeforeRenderPdf() {
    return this._onBeforeRenderPdf;
  }

  set onBeforeRenderPdf(value) {
    this._onBeforeRenderPdf = value;
  }

  setFont(font: FontFamily) {
    for (const subset of font.subsets) {
      if (subset.url) {
        const {
          fileName,
          base64,
          fontFamily,
          type = 'normal',
          fontWeight,
          encoding = ENCODING,
        } = subset;
        this.doc.addFileToVFS(fileName, base64);
        this.doc.addFont(fileName, fontFamily, type, fontWeight, encoding);
      }
    }
    this.doc.setFont(font.font);
  }

  async renderPDF(pdfContent: PDFContent, fileName, options?) {
    const { html } = pdfContent;
    await this.loadLib();
    const htmlIgnorer = new Html2PdfIgnorer(html);
    await breakPages(this.doc, html, PDF_SCALE_FACTOR, {
      header: {
        height: HEADER_PDF_HEIGHT,
      },
      footer: {
        height: FOOTER_PDF_HEIGHT,
      },
    });
    await this.onBeforeRenderPdf();
    await this.doc.html(html, {
      autoPaging: true,
      margin: [HEADER_PDF_HEIGHT, 0, FOOTER_PDF_HEIGHT, 0],
      html2canvas: {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        removeContainer: true,

        scale: PDF_SCALE_FACTOR,
        ignoreElements: (element) => htmlIgnorer.shouldIgnoreElement(element),
        onclone: (doc) => {
          const links = doc.querySelectorAll('.custom-link-pdf');
          let padding = 0;
          links.forEach((link) => {
            const rect = link.getBoundingClientRect();
            const anchorElm = link.querySelector('a');
            const url = anchorElm.getAttribute('href');
            this.doc.link(
              0,
              rect.top - rect.height + 8 - padding,
              this.doc.internal.pageSize.width,
              rect.height / 2,
              {
                url,
              }
            );
            // duy note: debug only below
            // this.doc.setFillColor(255, 0, 0);
            // this.doc.rect(
            //   0,
            //   rect.top - rect.height + 8 - padding,
            //   this.doc.internal.pageSize.width,
            //   rect.height / 2,
            //   'FD'
            // );
            padding += 12;
          });
        },
      },
      callback: (pdfObject) => {
        PDFService.pdfCallback(pdfObject, options).then(() => {
          this.doc.save(fileName);
        });
      },
    });
  }

  static async pdfCallback(pdfObject, options?) {
    const pdfPages = pdfObject.internal.pages;
    const pageHeight = pdfObject.internal.pageSize.height;
    const pageWidth = pdfObject.internal.pageSize.width;

    pdfPages.forEach((page, index) => {
      pdfObject.setPage(index);
      pdfObject.addImage({
        imageData: header,
        x: 0,
        y: 0,
        width: pageWidth,
        height: HEADER_COMPONENT_HEIGHT,
        ImageCompression: 'FAST',
      });
      pdfObject.link(0, 0, pageWidth, HEADER_COMPONENT_HEIGHT, { url: options?.header?.url });
      pdfObject.addImage({
        imageData: footer,
        x: 0,
        y: pageHeight - FOOTER_COMPONENT_HEIGHT,
        width: pageWidth,
        height: FOOTER_COMPONENT_HEIGHT,
        ImageCompression: 'FAST',
      });
      pdfObject.link(0, pageHeight - FOOTER_COMPONENT_HEIGHT, pageWidth, FOOTER_COMPONENT_HEIGHT, {
        url: options?.footer?.url,
      });
    });
  }
}

const useFontLoader = (fontFamily: FontFamily) => {
  const fontLoaderFns = React.useMemo(() => {
    return fontFamily.subsets.map(
      (font) => () =>
        axios
          .get(font.url, {
            responseType: 'arraybuffer',
          })
          .then((res) => Buffer.from(res.data, 'binary').toString('base64'))
    );
  }, [fontFamily.subsets]);
  const loadFont = React.useCallback(
    () => Promise.all(fontLoaderFns.map((fn) => fn())),
    [fontLoaderFns]
  );

  return { loadFont };
};

export const usePDFService = (font = DEFAULT_FONT) => {
  const pdfInstance = React.useMemo(() => new PDFService(), []);
  const [isFontLoaded, toggle] = useToggle(false);
  const { loadFont } = useFontLoader(font);
  React.useEffect(() => {
    pdfInstance.onBeforeRenderPdf = async () => {
      if (!isFontLoaded) {
        const data = await loadFont();

        font.subsets.forEach((subset, index) => {
          subset.base64 = data[index] as any;
        });
        pdfInstance.setFont(font);
        toggle();
      }
    };
  }, [font, font.subsets, isFontLoaded, loadFont, pdfInstance, toggle]);

  return pdfInstance;
};
